<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import { computed, ref, onMounted, onUnmounted, getCurrentInstance, watch } from 'vue'
import _ from 'lodash'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import UserService from '@/services/UserService';
import FormButton from '../Atoms/FormButton.vue';
import IconBase from '../icon/IconBase.vue';
import IconChevronLeft from '../icon/IconChevronLeft.vue';
import MainSidebarLogo from './MainSidebarLogo.vue';
import IconChevronUpDown from '../icon/IconChevronUpDown.vue';
import IconCheckmark from '../icon/IconCheckmark.vue';
import Spinner from '../ui/Spinner.vue';
import IconHandleVertical from '../icon/IconHandleVertical.vue'
import IconCross from '../icon/IconCross.vue'
import MainSidebarNavList from './MainSidebarNavList.vue'
import ProjectSwitcher from '../project/ProjectSwitcher.vue';
import DropDownMenu from '../Atoms/DropDownMenu.vue';
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue';
import AlertModal from "@/components/modals/AlertModal.vue";
import Sidebar from '../modals/Sidebar.vue'

const props = defineProps({
  mediaPulse: {
    type: Object,
  },
})

const instance = getCurrentInstance();
const root = instance.proxy.$root;
const route = useRoute()
const router = useRouter()
const store = useStore()

// Guest/team-access context used by the team-access alert modals below.
const guestTeamName = computed(() => store.state.User?.guestData?.teamName || '')
const guestOwnerEmail = computed(() => store.state.User?.guestData?.ownersEmail || '')

const memberDetails = computed(() => store.getters['User/memberDetails'])
const userLoaded = computed(() => store.getters['User/userLoaded'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const userId = computed(() => store.getters['User/userId'])
const subscriptions = computed(() => store.getters['User/subscriptions'])
const expanded = computed(() => store.getters['Ui/sidebarExpanded'])

const windowWidth = ref(window.innerWidth);

const updateWidth = () => {
  windowWidth.value = window.innerWidth;
};

const isMobile = computed(() => windowWidth.value < 768)

// What the header row renders. `Ui/sidebarExpanded` is the desktop rail
// preference and it is persisted (vuex-persist), so a sidebar collapsed on a
// laptop used to arrive on the phone as a 64px bar: workspace tile only, the
// hamburger pulled in behind it, the rest of the row empty. Below `md` the bar
// is never a rail — it always gets the full-width, labelled controls.
const barExpanded = computed(() => expanded.value || isMobile.value)

const OWN_ACCOUNT_LABEL = 'Your account'

// The account eyebrow: a slim muted line above the workspace row that names the
// account the session is in. Expanded it is text + chevron; collapsed, a small
// initial tile; on the mobile bar, a compact chip beside the workspace row.
const eyebrowClasses = computed(() => (
  barExpanded.value
    ? 'inline-flex max-w-full items-center gap-1 !rounded-md !px-1.5 !py-0.5 !h-6 box-border text-[11px] font-medium text-surface-7 hover:text-white hover:bg-surface-2 hover:no-underline transition-colors'
    : 'size-6 inline-flex items-center justify-center !rounded-md bg-surface-2 hover:bg-surface-3 transition-colors box-border hover:no-underline !p-0 mx-auto text-[10px] font-semibold uppercase text-surface-8'
))

const teamDropdown = ref(null)
// Derive the label straight from the reactive active-team object so it always
// reflects the real team (no separate network call / manual state to drift).
const selectedTeam = computed(() => activeTeam.value?.name || OWN_ACCOUNT_LABEL)

const inTeam = computed(() => !!store.state.User?.activatedTeam)
const hasOtherAccounts = computed(() => memberDetails.value.length > 0)

// Which account you are in only needs saying when there is more than one you
// could be in: inside someone else's team, or on your own account while also
// invited elsewhere. The solo owner (most users) gets the workspace row alone.
const showAccountEyebrow = computed(() => inTeam.value || hasOtherAccounts.value)

// To avoid the layout shift the conditional eyebrow would otherwise cause, we
// remember whether the user had other accounts last session and reserve the
// line during load.
const hadTeamsCached = ref(localStorage.getItem('castr:hasTeams') === '1')
const reserveEyebrowSpace = computed(() => !showAccountEyebrow.value && hadTeamsCached.value && !userLoaded.value)

const isOwnAccountActive = computed(() => !inTeam.value)
const isTeamActive = (team) => !!activeTeam.value && (activeTeam.value.id === team.id || activeTeam.value.name === team.name)

// Optimistic switch state: while a switch is in-flight we show the team the user
// picked and a spinner, so the switcher stays stable instead of flickering
// through the intermediate states (state reset → reload → access checks).
const switching = ref(false)
const pendingTeamName = ref(null)

const displayName = computed(() => (switching.value && pendingTeamName.value) ? pendingTeamName.value : selectedTeam.value)
const teamInitial = computed(() => (displayName.value || OWN_ACCOUNT_LABEL).trim().charAt(0).toUpperCase())
const isBillingAccess = ref(false)
const userInformation = ref(null)
const user = ref(null)

const mobileSidebar = ref(null)

const toggleSidebar = () => {
  if (isMobile.value) {
    store.dispatch('Ui/expandSidebar');
    mobileSidebar.value.toggleSidebar()
  } else {
    store.dispatch('Ui/toggleSidebarExpanded');
  }
}

onMounted(async () => {
  window.addEventListener('resize', updateWidth);
  await store.dispatch('User/getinfoMember', null, { root: true });
  user.value = await UserService.getUser(activeTeam.value?.id)
  checkAccess()
})

// Remove event listener on unmount
onUnmounted(() => {
  window.removeEventListener('resize', updateWidth);
});

watch(() => [activeTeam.value, userLoaded.value], async () => {
  if (userLoaded.value) {
    await setUser()
    await setTeam()
  }
})

// While a switch is running, changeTeam() owns access enforcement so the global
// watchers don't fire a competing setDefaultTeam()/modal mid-flight (the cause
// of the glitch). They resume normal duty once switching settles.
watch(() => store.state.User?.memberDeleted, () => { if (!switching.value) enforceAccess() })
watch(() => store.state.User?.memberDisabled, () => { if (!switching.value) enforceAccess() })
watch(() => subscriptions.value, () => { if (!switching.value) enforceAccess() }, { deep: true })

watch(() => route.path, async () => {
  mobileSidebar.value?.closeSidebar()
})

// Persist whether the user has teams so the next load can reserve the switcher
// slot (no shift) for team users, and skip it entirely for solo users.
watch([memberDetails, userLoaded], () => {
  if (memberDetails.value.length > 0) {
    hadTeamsCached.value = true
    try { localStorage.setItem('castr:hasTeams', '1') } catch (e) { /* ignore */ }
  } else if (userLoaded.value) {
    hadTeamsCached.value = false
    try { localStorage.setItem('castr:hasTeams', '0') } catch (e) { /* ignore */ }
  }
}, { immediate: true })

// Single source of truth: returns the alert-modal id to show when the current
// team isn't accessible, or null when everything is fine.
const evaluateAccessModal = () => {
  const U = store.state.User
  if (!activeTeam.value) return null
  if (U.memberDeleted) return 'deleted-member'
  if (U.memberDisabled) return 'disabled-paused'
  if (subscriptions.value?.length > 0) {
    const enabledSubs = subscriptions.value.filter((s) => s.enabled)
    const allowedTeams = checkOwnerTeamNum()
    if (enabledSubs.length === 0 || !allowedTeams) return 'expired-owner'
  }
  return null
}

// Enforce access: if blocked, revert to the personal dashboard and surface the
// relevant modal. Returns true when access was blocked.
const enforceAccess = () => {
  const modalId = evaluateAccessModal()
  if (modalId) {
    setDefaultTeam()
    showModal(modalId)
    return true
  }
  return false
}

const checkAccess = async () => {
  enforceAccess()
}

const checkOwnerTeamNum = () => {
  let numofTeam = []
  _.map(subscriptions.value, function (single) {
    if (single.enabled) {
      let streamCountoverride = _.get(single, ['definitionOverride', 'numOfTeamMembers']);
      if (streamCountoverride) {
        numofTeam.push(streamCountoverride);
      } else {
        numofTeam.push(_.get(single, ['package', 'definition', 'numOfTeamMembers']));
      }
    }
  })

  numofTeam = numofTeam.filter(function (el) {
    if (el && el != 0) {
      return el == +el;
    }
  });

  if (numofTeam.length) {
    return (numofTeam.length > 1 ? Math.max.apply(Math, numofTeam) : numofTeam[0])
  } else {
    return null;
  }
}

const changeTeam = async (name, id) => {
  if (!id) {
    console.error('changeTeam called with undefined id');
    return;
  }
  // Ignore re-entrant clicks and no-op when already on this team.
  if (switching.value) return;
  const currentId = activeTeam.value?.id || userId.value;
  if (id === currentId) {
    teamDropdown.value?.hide?.();
    return;
  }

  // Optimistic + atomic: lock the switch, show the picked team immediately, and
  // close the dropdown. The label stays put (no flicker) until we settle.
  switching.value = true;
  pendingTeamName.value = name;
  teamDropdown.value?.hide?.();

  emitAppEvent(APP_EVENTS.TEAM_CHANGING, true);
  store.dispatch('Ui/setLoadingStatus', true);

  try {
    await UserService.selectTeam(id);

    // Reset user state and force reload after team switch
    await store.dispatch('User/setdefaultUserstate', null, { root: true });
    await store.dispatch('User/loadUser', true, { root: true }); // Force refresh for team data
    await store.dispatch('User/getinfoOwner', true, { root: true });
    await store.dispatch('User/getinfoMember', true, { root: true });
    await UserService.setOwnerSession(store.state.User.ownermember);

    // If the freshly-loaded team isn't accessible (expired / disabled / removed),
    // surface the modal and stop here. Doing this inline — instead of letting the
    // global watchers race with the rest of this function — is what removes the
    // glitch: we don't load the team's streams or navigate behind the modal.
    if (enforceAccess()) return;

    // Force refresh streams for new team context
    await store.dispatch('Streams/getStreams', true);
    await setTeam();

    emitAppEvent(APP_EVENTS.TEAM_CHANGED, true);

    if ((window.location.href.indexOf("/app/paywall") > -1) || window.location.href.indexOf("/billing") > -1 || window.location.href.indexOf("/subscribe") > -1) {
      window.location.reload();
    }
    if (checkRedirect()) {
      router.push({ path: '/' });
    }
  } catch (error) {
    console.error('Team switch error:', error);
  } finally {
    store.dispatch('Ui/setLoadingStatus', false);
    switching.value = false;
    pendingTeamName.value = null;
  }
}
const checkRedirect = () => {
  const parts = window.location.pathname.split('/');
  let redirect = false;
  parts.map(function (p) {
    if ((p == 'multistreams') || (p == 'scheduled') || (p == 'videos') || (p == 'livestreams')) {
      redirect = true;
    }
  });
  return redirect;
}

const setUser = async () => {
  const userInfo = store.state.User;
  isBillingAccess.value = _.isBoolean(userInfo.isBillingAccess) ? userInfo.isBillingAccess : true;
  if (!userInfo.activeTeam) {
    userInformation.value = userInfo
  } else {
    const ownerUserInfo = await UserService.getTeamOwner(userInfo.activeTeam)
    ownerUserInfo.owner._id = userInfo._id;
    ownerUserInfo.owner.activeTeam = userInfo.activeTeam;
    userInformation.value = ownerUserInfo.owner;
  }
  // this.checkMenu(userInfo.activeTeam);
}

const setTeam = async () => {
  try {
    user.value = await UserService.getUser(activeTeam.value?.id);
  } catch (error) {
    console.error('[setTeam] Failed to load user:', error);
  }
}

const setDefaultTeam = async () => {
  const me = store.state.User;
  await UserService.selectTeam(me._id);
  await UserService.setOwnerSession(null);
}

const redirect = async () => {
  setTimeout(() => {
    router.push({ path: '/' });
    window.location.reload();
  }, 1000);
}

</script>

<template>
  <aside
    class="group/sidebar h-svh shrink-0 sticky z-30 top-0 !py-2 md:!pt-11 md:!pb-4 relative flex flex-col min-h-0 transition-all ease-in-out bg-surface-2 shadow md:bg-surface-darker md:shadow-[1px_0_20px_rgba(0,0,0,0.28)]"
    :class="barExpanded ? 'w-full md:w-[260px] basis-full md:basis-[260px] !px-4' : 'w-16 basis-16'">
    <!-- Collapse/expand handle: a solid edge button that fades in on sidebar hover -->
    <FormButton @click="toggleSidebar" :title="expanded ? 'Collapse sidebar' : 'Expand sidebar'"
      :aria-label="expanded ? 'Collapse sidebar' : 'Expand sidebar'"
      custom-classes="!hidden md:!flex absolute top-1/2 -translate-y-1/2 -right-3 z-30 size-6 items-center justify-center rounded-full bg-surface-3 ring-1 ring-surface-4 text-surface-8 shadow-md hover:bg-surface-4 hover:ring-surface-5 hover:text-white focus-visible:opacity-100 opacity-0 group-hover/sidebar:opacity-100 transition-all duration-200">
      <icon-base fill="none" class="transition-transform duration-200" :class="expanded ? 'rotate-0' : 'rotate-180'">
        <icon-chevron-left />
      </icon-base>
    </FormButton>
    <div class="flex !flex-row items-center gap-2 min-h-11 w-full shrink-0 md:!flex-col md:!items-stretch md:gap-0 md:min-h-0 md:mb-4">
      <MainSidebarLogo class="hidden md:block mx-auto shrink-0 md:mb-4" :expanded="expanded" />

      <!-- Account eyebrow: only when there is more than one account the user
           could be in. Names the current one; opens the account switcher. -->
      <DropDownMenu
        v-if="showAccountEyebrow"
        ref="teamDropdown"
        class="shrink-0 min-w-0 max-w-[45%] md:max-w-none md:mb-1"
        :class="expanded ? 'md:px-1' : 'md:mx-auto'"
        :custom-classes="eyebrowClasses"
        wrapper-class="min-w-[232px]"
        max-height="80"
        :aria-label="`Account: ${displayName}`"
        data-testid="account-eyebrow"
      >
        <template #toggle-button>
          <template v-if="barExpanded">
            <span class="min-w-0 truncate">{{ displayName }}</span>
            <Spinner v-if="switching" classes="shrink-0" spinner-color="var(--c-surface-6)" spinner-size="10px" />
            <icon-base v-else class="shrink-0 text-surface-6" :width="12" :height="12">
              <icon-chevron-up-down />
            </icon-base>
          </template>
          <span v-else :title="displayName">{{ teamInitial }}</span>
        </template>
        <template #menu-items>
          <p class="px-2.5 pt-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-surface-6 select-none mb-0">
            Accounts
          </p>
          <DropDownMenuItem v-if="userId" @click="changeTeam(OWN_ACCOUNT_LABEL, userId)"
            customClasses="!h-auto !py-2 !px-2.5 gap-x-2.5 hover:bg-surface-3 text-surface-text-1 hover:text-white flex items-center w-full rounded-md transition-colors">
            <span class="size-6 shrink-0 grid place-items-center rounded-md bg-surface-3 text-surface-8 text-[10px] font-semibold uppercase">Y</span>
            <span class="flex-1 min-w-0 truncate text-left text-tiny">{{ OWN_ACCOUNT_LABEL }}</span>
            <icon-base v-if="isOwnAccountActive" class="shrink-0 text-main-light">
              <icon-checkmark />
            </icon-base>
          </DropDownMenuItem>
          <DropDownMenuItem v-for="team in memberDetails" :key="team.id" @click="changeTeam(team.name, team.id)"
            customClasses="!h-auto !py-2 !px-2.5 gap-x-2.5 hover:bg-surface-3 text-surface-text-1 hover:text-white flex items-center w-full rounded-md transition-colors">
            <span class="size-6 shrink-0 grid place-items-center rounded-md bg-surface-3 text-surface-8 text-[10px] font-semibold uppercase">{{ (team.name || '?').charAt(0) }}</span>
            <span class="flex-1 min-w-0 truncate text-left text-tiny">{{ team.name }}</span>
            <icon-base v-if="isTeamActive(team)" class="shrink-0 text-main-light">
              <icon-checkmark />
            </icon-base>
          </DropDownMenuItem>
        </template>
      </DropDownMenu>
      <!-- Reserves the eyebrow's line during load for users who had other
           accounts last session, so the nav does not jump when it mounts. -->
      <div v-else-if="reserveEyebrowSpace" class="hidden md:block md:mb-1" :class="expanded ? 'px-2.5' : 'mx-auto'" aria-hidden="true">
        <div class="rounded bg-surface-3 animate-pulse" :class="expanded ? 'h-4 w-28' : 'size-6 rounded-md'"></div>
      </div>

      <!-- The workspace row: the primary control, one line. -->
      <div class="flex-1 min-w-0 md:flex-none md:w-full" data-testid="project-slot">
        <ProjectSwitcher :expanded="barExpanded" />
      </div>

      <FormButton isIcon type="link" aria-label="Open menu" class="transition-all !inline-flex md:!hidden shrink-0 ml-auto" size="md" @click="toggleSidebar">
        <icon-base class="" fill="none" :width="20" :height="20">
          <icon-handle-vertical />
        </icon-base>
      </FormButton>
    </div>

    <Sidebar v-if="isMobile" ref="mobileSidebar" :stage="1" stageOneCustomClass="!mt-6 h-svh flex flex-col" hide-header>
      <template v-slot:stage-one>
        <div class="flex items-center justify-center w-full bg-surface-3 shadow h-[72px] flex-shrink-0">
          <MainSidebarLogo class="relative top-0 left-4" :expanded="barExpanded" />
          <form-button class="absolute right-5 top-5" type="link" isIcon @click="toggleSidebar">
            <icon-base iconColor="none" class="">
              <icon-cross />
            </icon-base>
          </form-button>
        </div>
        <div class="flex-1 overflow-hidden pt-6">
          <MainSidebarNavList :expanded="barExpanded" />
        </div>
      </template>
    </Sidebar>
    <MainSidebarNavList v-else :expanded="expanded" />

    <alert-modal description="Access required" modal-id="deleted-member"
      message="You no longer have access to this team"
      ok-text="Got it" @modal-confirm="redirect">
      You can keep streaming from your own dashboard.
    </alert-modal>
    <alert-modal description="Access required" modal-id="disabled-paused"
      :message="guestTeamName ? ('“' + guestTeamName + '” is currently unavailable') : 'This team is currently unavailable'"
      ok-text="Got it" @modal-confirm="redirect">
      This team has been paused or disabled. Contact the team owner<template v-if="guestOwnerEmail">
        at <a class="team-alert-link" :href="'mailto:' + guestOwnerEmail">{{ guestOwnerEmail }}</a></template>
      for details.
    </alert-modal>
    <alert-modal description="Team's account has expired" modal-id="expired-owner"
      :message="guestTeamName ? ('Access to “' + guestTeamName + '” has expired') : 'This team’s access has expired'"
      ok-text="Go to my dashboard" @modal-confirm="redirect">
      This team’s subscription is no longer active. Ask the team owner<template v-if="guestOwnerEmail">
        at <a class="team-alert-link" :href="'mailto:' + guestOwnerEmail">{{ guestOwnerEmail }}</a></template>
      to reactivate it.
    </alert-modal>
    <alert-modal description="Access required" modal-id="downgraded-owner"
      :message="guestTeamName ? ('Access to “' + guestTeamName + '” is no longer available') : 'This team is no longer available'"
      ok-text="Go to my dashboard" @modal-confirm="redirect">
      The team owner’s plan no longer includes this access. Contact them<template v-if="guestOwnerEmail">
        at <a class="team-alert-link" :href="'mailto:' + guestOwnerEmail">{{ guestOwnerEmail }}</a></template>
      for details.
    </alert-modal>
  </aside>
</template>

<style scoped>
.team-alert-link {
  color: var(--c-main, #3e64ff);
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;
}
.team-alert-link:hover {
  text-decoration: underline;
}
</style>
